// src/pages/modules/AssetAssignPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchAssetAssignments,
  createAssetAssignment,
  updateAssetAssignment,
  deleteAssetAssignment,
  fetchStockItems,
  setPendingAssetAssignAck,     // ← NEW
  clearPendingAssetAssignAck,   // ← NEW
  acknowledgeAssetAssign,       // ← NEW
  clearError,
  fetchAllEmployees,
  fetchAllRequests,
  returnAssetAssignment,
} from 'store/slices/assetAssignSlice';
import { fetchInventoryDropdown, restoreRole } from "store/slices/commonSlice";
import { fetchAssetAssignLogs } from 'store/slices/assetAssignLogSlice';
import { Pencil, Trash2, MoreHorizontal, Loader2, Package, Users, FileText, CheckCircle, History, Clock, Undo2, MoreVertical, Plus, Eye } from 'lucide-react';
import DataTable from '../../components/common/DataTable';
import PageHeader from '../../components/common/PageHeader';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { ScrollArea } from 'components/ui/scroll-area';
import { usePermissions } from 'hooks/usePermissions';
import usePermissionSocket from 'hooks/usePermissionSocket';
import useIssuanceSocket from 'hooks/useIssuanceSocket';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Input } from 'components/ui/input';
import { cn } from 'lib/utils';
import { fetchEmployeeDropdown } from 'store/slices/commonSlice';
import { fetchRequestDropdown } from 'store/slices/commonSlice';
import { format, parseISO } from 'date-fns';
import { Card } from 'components/ui/card';
import FormField from 'components/common/FormField';
import { fetchActivityLogs, clearError as clearActivityLogError } from 'store/slices/activityLogSlice';
import LogDataDisplay from 'components/common/LogDataDisplay';
import { canManageEverything } from 'utlis/authUtils';
import { formatDateTime } from 'utlis/formateDateTime';

const AssetAssignPage = () => {
  useIssuanceSocket();
  usePermissionSocket();
  const isSuperAdmin = canManageEverything();

  const currentUser = useSelector(state => state.auth.user);
  const currentUserId = currentUser?.id;

  const dispatch = useDispatch();
  const { list: assignments, pagination, isLoading, error,
    // employees,       // ← NEW
    // requests,        // ← NEW
  } = useSelector((state) => state.assetAssign);
  const { canCreate, canUpdate, canDelete, canRead } = usePermissions('asset_assign');

  const employees = useSelector((state) => state.common.employees.items);

  const requests = useSelector((state) => state.common.requests.items);

  const { logs: assignLogs, isLoading: logsLoading, error: logsError } = useSelector((state) => state.activityLog);

  const stockItems = useSelector(
    (state) => state.common.inventoryDropdown.items
  );


  const [errors, setErrors] = useState({
    reason: '',
    employee: '',
    items: [],
  });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [currentAssignment, setCurrentAssignment] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState(null);
  const [reason, setReason] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [requestId, setRequestId] = useState('');
  const [items, setItems] = useState([{ inventory: '', quantity: '' }]);
  const [returnConfirm, setReturnConfirm] = useState({
    open: false,
    assignment: null,
  });

  const [ackNotes, setAckNotes] = useState("");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const { pendingAcknowledgement } = useSelector((state) => state.assetAssign);

  const refetch = useCallback(() => {
    dispatch(fetchAssetAssignments({ page, pageSize, search: searchTerm }));
  }, [dispatch, page, pageSize, searchTerm]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    // dispatch(fetchAllEmployees());
    dispatch(fetchEmployeeDropdown({ exclude_self: true }));
    // dispatch(fetchAllRequests());
    dispatch(fetchRequestDropdown());
    dispatch(fetchStockItems());
    dispatch(fetchInventoryDropdown());
  }, [dispatch]);


  useEffect(() => {
    if (error) {
      toast.error(typeof error === 'string' ? error : 'An error occurred');
      dispatch(clearError());
    }
  }, [error, dispatch]);


  const openDialog = (assign = null) => {
    if (assign) {
      if (!canUpdate) return toast.error("No permission to edit");
      setCurrentAssignment(assign);
      setReason(assign.reason || '');
      setEmployeeId(assign.employee || '');
      setRequestId(assign.request || '');
      setItems(assign.items.map(i => ({
        inventory: i.inventory,
        quantity: i.quantity,
      })));
    } else {
      if (!canCreate) return toast.error("No permission to create");
      setCurrentAssignment(null);
      setReason('');
      setEmployeeId('');
      setRequestId('');
      setItems([{ inventory: '', quantity: '' }]);
    }
    setIsDialogOpen(true);
  };

  const handleAddItem = () => {
    setItems(prev => [...prev, { inventory: '', quantity: '' }]);
  };

  const handleRemoveItem = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    setItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset all previous errors
    setErrors({ reason: '', employee: '', items: [], general: '' });

    let hasError = false;

    // ─── 1. Client-side basic validation ───
    if (!reason.trim()) {
      setErrors((prev) => ({ ...prev, reason: 'Reason is required' }));
      hasError = true;
    }

    if (!employeeId) {
      setErrors((prev) => ({ ...prev, employee: 'Please select an employee' }));
      hasError = true;
    }

    // Items basic check (inventory selected)
    const newItemErrors = items.map((item, idx) => {
      if (!item.inventory) return 'Please select an item';
      // You can add more client-side checks here if needed
      return '';
    });

    if (newItemErrors.some(Boolean)) {
      setErrors((prev) => ({ ...prev, items: newItemErrors }));
      hasError = true;
    }

    if (hasError) {
      toast.error("Please fix the errors above");
      return;
    }

    // ─── 2. Prepare payload ───
    const payload = {
      reason: reason.trim(),
      employee: employeeId,
      request: requestId || undefined,
      items: items.map((i) => ({
        inventory: i.inventory,
        // quantity: parseInt(i.quantity) || 1, // fallback if quantity missing
      })),
    };

    try {
      if (currentAssignment) {
        await dispatch(updateAssetAssignment({ id: currentAssignment.id, ...payload })).unwrap();
        toast.success("Assignment updated successfully");
      } else {
        await dispatch(createAssetAssignment(payload)).unwrap();
        toast.success("Asset assignment created successfully");
      }

      refetch();
      setIsDialogOpen(false);
    } catch (err) {
      console.error("Submission error:", err);

      // ─── 3. Handle backend validation errors ───
      let shown = false;

      // Case 1: Structured per-item errors (most common for stock)
      if (err?.items && Array.isArray(err.items)) {
        const itemErrors = Array(items.length).fill('');
        err.items.forEach((errorObj) => {
          // Assuming backend sends index or we match by order
          // If backend gives index → use it
          // Here assuming order-based (common pattern)
          if (errorObj.error?.[0]) {
            // Show first error message for that row
            // You can improve this if backend sends row index
            itemErrors[0] = errorObj.error[0]; // adjust logic if needed
          }
        });

        setErrors((prev) => ({ ...prev, items: itemErrors }));
        toast.error("Some items exceed available stock");
        shown = true;
      }

      // Case 2: General detail message (e.g. "Cannot edit after processed")
      if (err?.detail && typeof err.detail === 'string') {
        setErrors((prev) => ({ ...prev, general: err.detail }));
        toast.error(err.detail);
        shown = true;
      }

      // Fallback: unknown error
      if (!shown) {
        const fallbackMsg =
          err?.message || err?.detail || "Failed to save assignment. Please try again.";
        setErrors((prev) => ({ ...prev, general: fallbackMsg }));
        toast.error(fallbackMsg);
      }
    }
  };

  const handleDelete = async () => {
    if (!canDelete) return toast.error("No permission to delete");
    try {
      await dispatch(deleteAssetAssignment(currentAssignment.id)).unwrap();
      toast.success("Request deleted");
      refetch();
      setIsDeleteDialogOpen(false);
    } catch (err) {
      console.error(err);
      toast.error(
        typeof err === "string" ? err : "Failed to delete request"
      );
    }

  };

  // const isPendingAckForMe = (assign) => {
  //   console.log("ass", assign[0], assign.employee_details.id === loggedInId)
  //   return assign.employee_details.id === loggedInId
  // };

  const openHistory = async (assignment) => {
    setSelectedAssignmentId(assignment.id);
    setHistoryOpen(true);

    // Use general activity log API with resource=asset_assign & object_id
    dispatch(fetchActivityLogs({
      page: 1,
      page_size: 50,
      resource: 'asset_assign',
      object_id: assignment.id,
    }));
  };

  const loggedInUser = JSON.parse(localStorage.getItem("employee") || "{}");
  const loggedInId = loggedInUser?.id;

  const isPendingAckForMe = useCallback((assign) => {
    return (
      assign.status === "ack_pending" &&
      assign.employee_details?.id === loggedInId &&
      assign.created_by !== loggedInId &&        // ← THIS LINE IS THE KEY
      !assign.is_acknowledged
    );
  }, [loggedInId]);

  const columns = [
    {
      accessorKey: 'reason',
      header: 'Reason',
      cell: ({ row }) => <span className="font-medium">{row.original?.reason || '-'}</span>,
    },
    // {
    //   header: 'Items',
    //   cell: ({ row }) => (
    //     <div className="space-y-1">
    //       {row.original.items.map((item, idx) => (
    //         <div key={idx} className="text-sm">
    //           <Badge variant="outline" className="mr-2">{item.quantity}x</Badge>
    //           {item.inventory_details?.name || 'Unknown Item'}
    //         </div>
    //       ))}
    //     </div>
    //   ),
    // },
    {
      header: 'Items',
      cell: ({ row }) => (
        <div className="space-y-2">
          {(row.original.items || []).map((item, idx) => {
            const d = item.inventory_details || {};

            return (
              <div
                key={idx}
                className="p-2 rounded-md border bg-muted/40 text-sm space-y-1"
              >
                <div className="font-medium">
                  {d.name || 'Unknown Item'}
                </div>
                <div className="text-xs text-muted-foreground space-y-1">
                  {d.category && (
                    <div>
                      <span className="font-medium text-foreground">Category:</span>{' '}
                      {d.category}
                    </div>
                  )}

                  {d.brand_name && (
                    <div>
                      <span className="font-medium text-foreground">Brand Name:</span>{' '}
                      {d.brand_name}
                    </div>
                  )}

                  {d.model_no && (
                    <div>
                      <span className="font-medium text-foreground">Model No:</span>{' '}
                      {d.model_no}
                    </div>
                  )}

                  {d.serial_no && (
                    <div>
                      <span className="font-medium text-foreground">Serial No:</span>{' '}
                      {d.serial_no}
                    </div>
                  )}
                </div>


                {/* {typeof d.current_stock === 'number' && (
              <Badge variant="outline" className="mt-1">
                Available: {d.current_stock}
              </Badge>
            )} */}
              </div>
            );
          })}
        </div>
      ),
    },

    {
      accessorKey: 'status_display',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={row.original.status_display === 'pending' ? 'warning' : row.original.accepted ? 'success' : 'secondary'}>
          {row.original.status_display.charAt(0).toUpperCase() + row.original.status_display.slice(1)}
          {row.original.accepted && ' · Accepted'}
        </Badge>
      ),
    },
    {
      accessorKey: 'employee__name',
      header: 'Assigned To',
      cell: ({ row }) => <span className="text-sm">{row.original.employee_details.name ?? '-'}</span>,
    },
    {
      accessorKey: 'created_at',
      header: 'Assigned On',
      cell: ({ row }) => <span className="text-sm">{formatDateTime(row.original.created_at) ?? '-'}</span>,
    },
    {
      accessorKey: 'created_by_name',
      header: 'Assigned By',
      cell: ({ row }) => <span className="text-sm">{row.original.created_by_name ?? '-'}</span>,
    },

    //  {
    //   accessorKey: 'updated_at',
    //   header: 'Updated On',
    //   cell: ({ row }) => <span className="text-xs">{formatDateTime(row.original.updated_at) ?? '-'}</span>,
    // },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const assign = row.original;

        const needsAck = isPendingAckForMe(assign);

        const isPending = assign.status === "pending" ;
        const isAssigned = assign.status === "assigned" || assign.status === "acknowledged";
        const totalAssigned = assign.items.reduce((s, i) => s + i.quantity, 0);
        const alreadyReturned = assign.returned_quantity || 0;
        const canReturn = assign.status === "assigned";

        const hasAnyAction = canUpdate || canDelete || (canUpdate && canReturn) || needsAck;


        const isAssignedToMe = assign.employee_details?.id === currentUserId ||
          assign.employee === currentUserId;



        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon"><MoreVertical className="h-4 w-4" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {(canUpdate || isSuperAdmin) && isPending && (
                <DropdownMenuItem onClick={() => openDialog(assign)}>
                  <Pencil className="mr-2 h-4 w-4" /> Edit
                </DropdownMenuItem>
              )}
              {(canDelete || isSuperAdmin) && (
                <DropdownMenuItem onClick={() => { setCurrentAssignment(assign); setIsDeleteDialogOpen(true); }} className="text-destructive">
                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                </DropdownMenuItem>
              )}
              {canUpdate && canReturn && isAssignedToMe && (
                <DropdownMenuItem
                  onClick={() => setReturnConfirm({ open: true, assignment: assign })}
                  className="text-orange-600 font-medium"
                >
                  <Undo2 className="mr-2 h-4 w-4" /> Return Asset
                </DropdownMenuItem>
              )}
              {(canUpdate || isSuperAdmin) && (
                <DropdownMenuItem onClick={() => openHistory(assign)}>
                  <History className="mr-2 h-4 w-4" /> Assignment History
                </DropdownMenuItem>
              )}
              {needsAck && (
                <DropdownMenuItem
                  onClick={() => {
                    dispatch(setPendingAssetAssignAck({
                      id: assign?.id,
                      data: assign
                    }));
                  }}
                  className="text-green-600 font-medium"
                >
                  <CheckCircle className="mr-2 h-4 w-4" /> Acknowledge Receipt
                </DropdownMenuItem>
              )}
              {/* No actions available message */}
              {!hasAnyAction && (
                <DropdownMenuItem disabled className="text-muted-foreground italic">
                  No actions available
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Asset Assigments"
        description="Request IT assets from inventory"
        action={canCreate ? { label: 'Assign IT Asset', onClick: () => openDialog() } : null}
      />

      {/* {isLoading && !assignments.length ? (
        <div className="flex justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <span>Loading requests...</span>
        </div>
      ) : null} */}

      <DataTable
        data={assignments}
        totalCount={pagination.count}
        columns={columns}
        searchPlaceholder="Search by reason..."
        pageIndex={page - 1}
        pageSize={pageSize}
        pageCount={Math.ceil(pagination.count / pagination.page_size)}
        onPageChange={(p) => setPage(p + 1)}
        onPageSizeChange={setPageSize}
        onSearch={setSearchTerm}
        isLoading={isLoading}
        showExport={false}
        showImport={false}
        onDelete={async (id) => {
          await dispatch(deleteAssetAssignment(id));
          refetch();
        }}
        onRestore={async (id) => {
          await dispatch(restoreRole({ entityType: 'asset-assigns', id }));
          refetch();
        }}
      />


      {/* Form Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-screen overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{currentAssignment ? 'Edit Asset Assign' : 'New Asset Assign'}</DialogTitle>
            <DialogDescription>
              {currentAssignment ? 'Update your asset request' : 'Request multiple items from IT stock'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">

            {/* REASON FIELD */}
            <div className="space-y-2">
              <Label>
                Remarks <span className="text-destructive">*</span>
              </Label>
              <Textarea
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  setErrors(prev => ({ ...prev, reason: '' }));
                }}
                placeholder="e.g., New joinee setup, replacement..."
                className={cn(errors.reason && "border-red-500 focus:ring-red-500")}
              />
              {errors.reason && <p className="text-sm text-destructive">{errors.reason}</p>}
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Employee <span className="text-destructive">*</span>
              </Label>

              <FormField
                name="employee"
                type="select"
                value={employeeId}
                onChange={(e) => {
                  setEmployeeId(e.target.value);
                  setErrors(prev => ({ ...prev, employee: '' })); // clear error on change
                }}
                options={
                  Array.isArray(employees) && employees.length > 0
                    ? employees.map((emp) => ({
                      value: emp.id.toString(),
                      label: emp.name || `${emp.first_name} ${emp.last_name} (${emp.code})`,
                    }))
                    : []
                }
                placeholder={
                  employees === null || employees === undefined
                    ? "Loading employees..."
                    : employees.length === 0
                      ? "No employees available"
                      : "Select employee"
                }
                disabled={!Array.isArray(employees) || employees.length === 0}
                required
                error={errors.employee}
              />
            </div>

            {/* ITEMS LIST */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <Label>Items to Assign <span className="text-destructive">*</span></Label>
                <Button type="button" size="sm" onClick={handleAddItem}>
                  <Package className="h-4 w-4 mr-2" /> Add Asset
                </Button>
              </div>

              <div className="space-y-4">
                {items.map((item, idx) => (
                  <div key={idx} className="flex gap-3 items-start">
                    <div className="flex-1 space-y-2">
                      <FormField
                        type="select"
                        value={item.inventory || ''}
                        onChange={(e) => {
                          handleItemChange(idx, 'inventory', e.target.value);
                          // Clear error for this row
                          setErrors(prev => ({
                            ...prev,
                            items: prev.items.map((err, i) => i === idx ? '' : err),
                          }));
                        }}
                        options={stockItems.map(stock => ({
                          value: stock.id.toString(),
                          label: `${stock.sub_category__name}${stock.brand_name ? ` - ${stock.brand_name}` : ''
                            }${stock.model_no ? ` - ${stock.model_no}` : ''
                            }${stock.serial_no ? ` - ${stock.serial_no}` : ''
                            }`,

                        }))}
                        placeholder="Search stock item..."
                        error={Array.isArray(errors.items) && errors.items[idx]?.includes('stock')}
                        disabled={stockItems.length === 0}
                      />
                    </div>

                    {/* <Input
                      type="number"
                      min="1"
                      placeholder="Qty"
                      className={cn(
                        "w-24",
                        Array.isArray(errors.items) && errors.items[idx]?.includes('quantity') && "border-red-500 focus:ring-red-500"
                      )}
                      value={item.quantity}
                      onChange={(e) => {
                        handleItemChange(idx, 'quantity', e.target.value);
                        // Clear error for this row
                        setErrors(prev => ({
                          ...prev,
                          items: prev.items.map((err, i) => i === idx ? '' : err),
                        }));
                      }}
                    /> */}

                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      onClick={() => handleRemoveItem(idx)}
                      disabled={items.length === 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}

                {/* Global error – ONLY shown if there is an actual error */}
                {/* {Array.isArray(errors.items) && errors.items.some(err => err.length > 0) && (
                  <p className="text-sm text-destructive !mt-2">
                    Please select item and enter valid quantity for all rows
                  </p>
                )} */}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">
                {currentAssignment ? 'Update' : 'Create'} Assignment
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Request?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete the request for <strong>{currentAssignment?.reason}</strong>.
              <br /><br />
              <strong>This action cannot be undone.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* ASSET ASSIGN ACKNOWLEDGEMENT MODAL */}

      <Dialog
        open={!!pendingAcknowledgement?.open}
        onOpenChange={(open) => !open && dispatch(clearPendingAssetAssignAck())}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-6 w-6 text-orange-500" />
              Confirm Asset Receipt
            </DialogTitle>
            <DialogDescription>
              You have been assigned assets: <strong>{pendingAcknowledgement?.assign?.reason}</strong>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="font-medium">Assigned Items</Label>
              <div className="mt-2 space-y-2">
                {pendingAcknowledgement?.assign?.items?.map((item, i) => (
                  <div key={i} className="flex justify-between p-3 bg-muted rounded-lg text-sm">
                    <span>{item.inventory_details?.name || "Unknown Item"}</span>
                    <Badge>{item.quantity}x</Badge>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>Notes (optional for accept, required for reject)</Label>
              <Textarea
                placeholder="e.g., All items received in good condition..."
                value={ackNotes}
                onChange={(e) => setAckNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              onClick={async () => {
                if (!ackNotes.trim()) return toast.error("Please provide a reason for rejection");
                try {
                  await dispatch(acknowledgeAssetAssign({
                    id: pendingAcknowledgement.assignId,
                    is_accepted: false,
                    remark: ackNotes
                  })).unwrap();
                  toast.success("Assignment rejected");
                  dispatch(clearPendingAssetAssignAck());
                  setAckNotes("");
                } catch {
                  toast.error("Failed to reject");
                }
              }}
            >
              Reject
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={async () => {
                try {
                  await dispatch(acknowledgeAssetAssign({
                    id: pendingAcknowledgement.assignId,
                    is_accepted: true,
                    remark: ackNotes || "Received in good condition"
                  })).unwrap();
                  toast.success("Assets acknowledged successfully");
                  dispatch(clearPendingAssetAssignAck());
                  setAckNotes("");
                } catch {
                  toast.error("Failed to acknowledge");
                }
              }}
            >
              Accept & Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* SIMPLE RETURN CONFIRMATION MODAL */}
      <AlertDialog open={returnConfirm.open} onOpenChange={(o) => !o && setReturnConfirm({ open: false, assignment: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Return Asset?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to return this assigned asset?
              <br /><br />
              <strong>{returnConfirm.assignment?.reason}</strong>
              <div className="mt-3 text-sm space-y-1">
                {returnConfirm.assignment?.items.map((item, i) => (
                  <div key={i}>
                    • {item.quantity}x {item.inventory_details?.name || "Item"}
                  </div>
                ))}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                try {
                  await dispatch(returnAssetAssignment(returnConfirm.assignment.id)).unwrap();
                  toast.success("Asset returned successfully");
                  setReturnConfirm({ open: false, assignment: null });
                  refetch();
                } catch (err) {
                  toast.error("Failed to return asset");
                }
              }}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Yes, Return Asset
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ← NEW: ASSET ASSIGNMENT HISTORY DIALOG (same style as RequestIssuancePage) */}
      {/* Asset Assignment History Dialog – Fully Dynamic Based on Real API Response */}
      {/* ASSET ASSIGNMENT HISTORY DIALOG – USING GENERAL ACTIVITY LOG API */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="text-xl">Assignment History</DialogTitle>
            <DialogDescription>
              Audit trail for assignment: <strong className="text-primary">
                #{selectedAssignmentId && assignLogs.find(l => l.object_id === selectedAssignmentId)?.object_repr || 'Loading...'}
              </strong>
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="max-h-[65vh] pr-4">
            {logsLoading ? (
              <div className="text-center py-16 text-muted-foreground">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3" />
                <p>Loading history...</p>
              </div>
            ) : assignLogs.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <History className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No activity recorded for this assignment</p>
              </div>
            ) : (
              <div className="space-y-6 py-4">
                {assignLogs
                  .filter(log => log.object_id === selectedAssignmentId && log.resource === 'asset_assign')
                  .map((log) => {
                    const by = log.created_by
                      ? `${log.created_by.first_name || ''} ${log.created_by.last_name || ''}`.trim() || log.created_by.code || 'Unknown'
                      : 'System';

                    return (
                      <Card key={log.id} className="p-6 hover:shadow-md transition-all border">
                        <div className="flex gap-4">
                          {/* Timeline */}
                          <div className="text-right text-xs text-muted-foreground shrink-0">
                            <p>{formatDateTime(log.created_at)}</p>
                            <p className="font-medium">{formatDateTime(log.created_at)}</p>
                          </div>

                          <div className="flex-1 space-y-4">
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="font-semibold text-lg">{log.action_display || log.action_type}</p>
                                <p className="text-sm text-muted-foreground">by <strong>{by}</strong></p>
                                {log.created_by?.code && (
                                  <p className="text-xs text-muted-foreground">({log.created_by.code})</p>
                                )}
                              </div>
                              <div className="flex items-center gap-3">
                                <Badge variant={
                                  log.action_type === 'create' ? 'success' :
                                    log.action_type === 'update' ? 'default' :
                                      log.action_type === 'return' ? 'warning' :
                                        'destructive'
                                }>
                                  {log.action_type.replace('_', ' ').toUpperCase()}
                                </Badge>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedLog(log);
                                    setDetailsOpen(true);
                                  }}
                                >
                                  <Eye className="h-4 w-4 mr-1" /> View Details
                                </Button>
                              </div>
                            </div>

                            {/* Summary */}
                            {log.summary && (
                              <p className="text-foreground">{log.summary}</p>
                            )}
                          </div>
                        </div>
                      </Card>
                    );
                  })}
              </div>
            )}
          </ScrollArea>

          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={() => setHistoryOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* DETAILED LOG MODAL – USING YOUR LogDataDisplay COMPONENT */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-5xl max-h-[95vh]">
          <DialogHeader>
            <DialogTitle className="text-2xl">Activity Details</DialogTitle>
            <DialogDescription>
              Full audit entry • {selectedLog && formatDateTime(selectedLog.created_at)}
            </DialogDescription>
          </DialogHeader>

          {selectedLog && (
            <ScrollArea className="max-h-[70vh] pr-4">
              <div className="space-y-8 py-6">

                {/* Header Summary */}
                <div className="bg-card border rounded-lg p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Performed by</p>
                      <p className="text-xl font-bold">
                        {selectedLog.created_by
                          ? `${selectedLog.created_by.first_name || ''} ${selectedLog.created_by.last_name || ''}`.trim() || selectedLog.created_by.code
                          : 'System'
                        }
                      </p>
                      {selectedLog.created_by?.code && (
                        <p className="text-sm text-muted-foreground">({selectedLog.created_by.code})</p>
                      )}
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="text-lg px-4 py-2">
                        {selectedLog.resource_display || selectedLog.resource}
                      </Badge>
                      <div className="mt-2">
                        <Badge variant={
                          selectedLog.action_type === 'create' ? 'success' :
                            selectedLog.action_type === 'update' ? 'default' :
                              selectedLog.action_type === 'return' ? 'warning' :
                                'destructive'
                        } className="text-lg px-4 py-2">
                          {selectedLog.action_display || selectedLog.action_type.replace('_', ' ').toUpperCase()}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <p className="text-sm font-medium text-muted-foreground">Summary</p>
                    <p className="text-lg mt-2 font-medium text-foreground">
                      {selectedLog.summary || 'No summary available'}
                    </p>
                  </div>
                </div>

                {/* MAIN LOG DATA – USING YOUR REUSABLE LogDataDisplay */}
                <LogDataDisplay logData={selectedLog.data} />

              </div>
            </ScrollArea>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailsOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default AssetAssignPage;